import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { AnalyticEvents } from '@app/enums';
import { IntegrationIndeed } from '@app/models/company/settings/integrations/indeed.model';
import { SettingsIntegration } from '@app/models/company/settings/settings-integration.model';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { IndeedIntegrationStatus } from '@applicant-tracker/enums/indeed-integration-status.enum';
import { JobPostingStatus } from '@applicant-tracker/enums/job-posting-status.enum';
import { IndeedValidationService } from '@applicant-tracker/services';
import { Company } from '@models/company/company.model';
import { Employer } from '@models/recruiting/indeed/employer.model';
import { JobPosting } from '@models/recruiting/job-posting.model';

@Component({
    templateUrl: './employer-select.template.html',
    styleUrls: ['./employer-select.style.scss'],
})
export class EmployerSelectView implements OnInit {
    isLoading = false;
    isSaving = false;
    employers: Employer[];
    integrationIndeed: IntegrationIndeed = new IntegrationIndeed();
    integration: SettingsIntegration;
    initialEmployerNumber?: string;
    isWarningModalOpen = false;
    isValidationModalOpen = false;
    confirmed = false;
    jobIds: number[] = [];
    failedCount = 0;
    employerChanged = false;
    showEmployerSelect = false;
    isIndeedJobSyncEnabled = true; // TODO save changes to company.isIndeedJobSyncEnabled

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        private router: Router,
        private indeedValidationService: IndeedValidationService,
        private analyticService: AnalyticService
    ) {}

    get canSave(): boolean {
        return !!this.integrationIndeed?.employerNumber;
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        try {
            const [employers] = await Employer.all();
            this.employers = employers;

            this.integrationIndeed = (
                await Company.with('indeedIntegration.integration').find(this.auth.company.id)
            ).indeedIntegration;

            this.integration = this.integrationIndeed.integration;
            this.integration.isEnabled = true;
            this.integrationIndeed.isEnabled = true;
            this.initialEmployerNumber = this.integrationIndeed.employerNumber;

            const [jobPostings] = await JobPosting.where('status', JobPostingStatus.Active).all();
            this.jobIds = jobPostings.map((j) => j.id);
            this.showEmployerSelect = !(this.employers.length === 1 && !!this.integrationIndeed.employerNumber);

            this.integrationIndeed.syncJobs = this.showEmployerSelect;
        } catch (e) {
            this.notify.error(e);
        }

        this.isLoading = false;
    }

    onProceed(): void {
        this.isValidationModalOpen = false;
        this.analyticService.trackEvent(AnalyticEvents.BulkSyncError);
        this.router.navigate(['/settings/integrations-v2']);
        this.notify.success(
            this.employerChanged
                ? 'Successfully updated Indeed integration details'
                : 'Successfully connected Indeed integration'
        );
    }

    async onSave(): Promise<void> {
        this.isSaving = true;

        this.employerChanged =
            !!this.initialEmployerNumber && this.initialEmployerNumber !== this.integrationIndeed.employerNumber;

        if (this.employerChanged && !this.confirmed) {
            this.isWarningModalOpen = true;
            return;
        }

        this.integrationIndeed.isEnabled = true;
        this.integrationIndeed.status = IndeedIntegrationStatus.Complete;

        try {
            await this.integrationIndeed.param('company', this.auth.company.id).save();
            await this.integration.param('company', this.auth.company.id).save();
        } catch (e) {
            this.notify.error(e);
            return;
        } finally {
            this.isSaving = false;
        }

        if (!this.showEmployerSelect) {
            this.integrationIndeed.syncJobs = false;
        }

        if (this.integrationIndeed.syncJobs && this.jobIds.length > 0) {
            try {
                const validation = await this.indeedValidationService.validateCreateById(
                    this.jobIds,
                    this.integrationIndeed.employerNumber
                );

                if (validation.failedCount > 0) {
                    this.failedCount = validation.failedCount;
                    this.isValidationModalOpen = true;
                    return;
                }
            } catch (e) {
                this.notify.error(ErrorParser.parse(e));
            } finally {
                this.isSaving = false;
            }
        }

        if (!this.employerChanged) {
            this.analyticService.trackEvent(AnalyticEvents.LinkingAnExistingCompany);
        }

        this.router.navigate(['/settings/integrations-v2']);
        this.notify.success(
            this.employerChanged
                ? 'Successfully updated Indeed integration details'
                : 'Successfully connected Indeed integration'
        );
    }
}
